import {IssueStatus} from "../../../domain/entities/issue-status.entity";
import {IssueType} from "../../../domain/entities/issue-type.entity";
import {isAbsenceFamily, rootUidOf} from "../../../domain/helpers/issue-type-root.helper";

/**
 * True for a type of the Absence or the Public holiday family — the two roots and their children
 * (behaviour follows the root, D3). An absence records time off, not a task to work off: the dialog
 * hides status, board, labels and description for it. Pulled out as its own pure predicate so the
 * orchestrator's subscriptions stay unit-testable without the surrounding Lit/tsyringe machinery.
 */
export function isAbsenceType(type: IssueType | null | undefined): boolean {
    return isAbsenceFamily(rootUidOf(type));
}

/**
 * True when the dialog's issue type carries a status and the status field is empty — then the status
 * becomes OPEN (Stefan, 2026-10-18: a status is set to OPEN only for a type that carries one). Reached
 * when an issue without a status (a statusless issue, or its clone) is switched to such a type; an
 * existing status is kept.
 */
export function needsOpenStatus(type: IssueType | null | undefined, status: IssueStatus | null | undefined): boolean {
    return !!type && !type.isStatusless && !status;
}

/**
 * What a type change in the dialog does to the status field (D19, D22): a statusless type gets no status
 * ('clear'); a type with a status whose field is empty gets OPEN ('fill-open'); otherwise the status
 * stays ('keep'). The dialog no longer forces REMINDER on any type.
 */
export function statusAfterTypeChange(type: IssueType | null | undefined, status: IssueStatus | null | undefined): 'clear' | 'fill-open' | 'keep' {
    if (type?.isStatusless) {
        return 'clear';
    }
    return needsOpenStatus(type, status) ? 'fill-open' : 'keep';
}
